import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAtom } from "jotai";
import { Dialog, Transition } from "@headlessui/react";

import { useFeatureFlags } from "@/providers/featureFlags";
import { MobileNav } from "@/components/common/MobileNav/MobileNav";
import { mobileSearchActiveAtom } from "@/components/common/Search/atoms";
import { SearchMobile } from "@/components/common/Search/SearchMobile";
import {
  SidebarNav,
  SidebarNavProvider,
  useSidebarNav,
} from "@/components/common/SidebarNav";
import { IconButton } from "@/components/ui/IconButton";
import { Logo } from "@/components/ui/Logo";
import { ChevronRight24, Close20, Menu20 } from "@/components/ui/SvgIcon";

import cn from "@/utils/cn";

import { windowHeight } from "@/atoms";

type Props = {
  children: React.ReactNode;
};

export const ProtectedRouteLayout = ({ children }: Props) => {
  return (
    <SidebarNavProvider>
      <ProtectedRouteLayoutContent>{children}</ProtectedRouteLayoutContent>
    </SidebarNavProvider>
  );
};

const ProtectedRouteLayoutContent = ({ children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const [height, setHeight] = useAtom(windowHeight);
  const [mobileSearchActive, setMobileSearchActive] = useAtom(
    mobileSearchActiveAtom,
  );
  const { isCollapsed, toggleSidebar } = useSidebarNav();
  const { loading: featureFlagsLoading } = useFeatureFlags();
  const closeMobileSearch = () => {
    setMobileSearchActive(false);
  };

  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  function open() {
    setIsOpen(true);
  }
  function close() {
    setIsOpen(false);
  }

  const isMobileHome = () => {
    return pathname == "/app" || pathname == "/app/";
  };

  useEffect(() => {
    if (mobileSearchActive) document.body.style.overflowY = "hidden";
    else document.body.style.overflowY = "auto";
  }, [mobileSearchActive]);

  const updateHeight = useCallback(
    () => setHeight(window.innerHeight),
    [setHeight],
  );

  React.useEffect(() => {
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [updateHeight]);

  const cssVars = {
    "--vh": height + "px",
  } as React.CSSProperties;

  // Collapsible width (112px when collapsed, 280px when expanded)
  const sidebarWidth = isCollapsed ? 112 : 280;
  // Add 32px (xl:pl-8) padding between sidebar and content
  const contentPaddingLeft = sidebarWidth + 32;

  if (featureFlagsLoading) return null;

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className="z-50 hidden  border-chorus-black-100 transition-all duration-300 ease-in-out xl:fixed xl:inset-y-0 xl:flex xl:flex-col"
        style={{ width: `${sidebarWidth}px` }}
      >
        <SidebarNav />
      </div>

      {/* Expand button - rendered outside sidebar for proper z-index stacking */}
      <button
        onClick={toggleSidebar}
        className={cn(
          "absolute top-11 z-[100] hidden size-8 items-center justify-center rounded-xl bg-chorus-black-500 shadow-md transition-all duration-300 hover:bg-chorus-black-400 xl:fixed xl:flex",
          isCollapsed
            ? "pointer-events-auto translate-x-0 scale-100 opacity-100"
            : "pointer-events-none -translate-x-4 scale-0 opacity-0",
        )}
        style={{
          left: `${sidebarWidth - 18}px`,
          transition:
            "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1), left 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        aria-label="Expand sidebar"
        data-testid="sidebar-expand-button"
      >
        <ChevronRight24
          className={cn(
            "fill-chorus-gray-300 transition-transform duration-300",
            isCollapsed ? "rotate-0" : "rotate-180",
          )}
        />
      </button>

      {/* Animated sidebar for mobile */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[11] xl:hidden" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 z-[999997] bg-chorus-black-400/80" />
          </Transition.Child>
          <div className="fixed inset-0 z-[999998] flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-150 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-150 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-150"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-4">
                    <IconButton
                      icon={<Close20 />}
                      variant="link"
                      onClick={close}
                      className="text-white"
                    />
                  </div>
                </Transition.Child>
                <SidebarNav />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Sticky Top bar for mobile */}
      <div
        className={cn(
          "sticky top-0 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-chorus-black-500 px-4 shadow-sm max-md:hidden sm:px-6 xl:hidden xl:px-8",
          isOpen ? "z-auto" : "z-[9999999999]",
        )}
      >
        <div className="flex flex-1 gap-x-4 self-stretch xl:gap-x-6">
          <div className="ml-[2px] flex h-16 shrink-0 items-center">
            <Logo width={106} />
          </div>
        </div>
        <IconButton
          variant="link"
          onClick={open}
          icon={<Menu20 />}
          className="mr-[-6px] text-white"
        />
      </div>
      {/* mobile bottom nav */}
      {!isMobileHome() && <MobileNav />}
      {mobileSearchActive && <SearchMobile close={closeMobileSearch} />}

      <div
        className="transition-all duration-300 xl:pr-2"
        style={
          {
            ...cssVars,
            "--sidebar-width": `${sidebarWidth}px`,
            "--content-padding-left": `${contentPaddingLeft}px`,
          } as React.CSSProperties
        }
      >
        <style>{`
          @media (min-width: 1280px) {
            .main-content-wrapper {
              padding-left: var(--content-padding-left) !important;
              transition: padding-left 300ms ease-in-out;
            }
          }
        `}</style>
        <main
          className={cn(
            "main-content-wrapper",
            isMobileHome()
              ? "flex min-h-screen flex-col bg-[#F1F5F9]"
              : "p-4 pb-24 sm:px-6 sm:pt-6 md:pb-8 md:pt-0 xl:px-8",
          )}
        >
          {children}
        </main>
      </div>

      <div className="fixed inset-y-0 right-0 z-10 w-2 bg-chorus-black-500 max-xl:hidden"></div>
      <div
        className="fixed bottom-0 right-0 z-10 h-2 bg-chorus-black-500 transition-all duration-300 max-xl:hidden"
        style={{ left: `${sidebarWidth}px` }}
      >
        <div className="absolute -left-1 bottom-1 size-4 -scale-y-100 bg-[url('/src/assets/images/top-corner-radius.svg')]"></div>
        <div className="absolute bottom-1 right-1 size-4 -scale-100 bg-[url('/src/assets/images/top-corner-radius.svg')]"></div>
      </div>
      <div
        className="fixed right-0 top-0 z-10 h-2 bg-chorus-black-500 transition-all duration-300 max-xl:hidden"
        style={{ left: `${sidebarWidth}px` }}
      >
        <div className="absolute -left-1 top-1 size-4 bg-[url('/src/assets/images/top-corner-radius.svg')]"></div>
        <div className="absolute right-1 top-1 size-4 -scale-x-100 bg-[url('/src/assets/images/top-corner-radius.svg')]"></div>
      </div>
    </>
  );
};
